import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import cn from 'classnames';
import { Box, ConfirmationModal, IconButton, Typography, useOverlayContext } from 'seagull';
import { Copy, Edit, PDF, Text, Trash } from 'seagull/icons';
import { ArrowSolidRight } from 'seagull/icons/arrows';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStore } from '~/contexts';
import { useDeleteCustomPageTemplate } from '~/hooks/reactQuery/mutations/useDeleteCustomPageTemplate';
import { useDuplicateCustomPageTemplate } from '~/hooks/reactQuery/mutations/useDuplicateCustomPageTemplate';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
import s from './CustomPageTemplateRow.module.scss';
export const GetCustomPageTemplateRowTID = (parentTestId)=>getTestIds(parentTestId);
function getTypeConfig(type, customPages) {
    switch(type){
        case 'video':
            return {
                icon: ArrowSolidRight,
                label: customPages.video(),
                iconWrapperClassName: s.videoIconWrapper
            };
        case 'pdf':
            return {
                icon: PDF,
                label: customPages.pdf(),
                iconWrapperClassName: s.pdfIconWrapper
            };
        case 'text':
            return {
                icon: Text,
                label: customPages.text(),
                iconWrapperClassName: s.textIconWrapper
            };
    }
}
export const CustomPageTemplateRow = (param)=>{
    let { template, onEdit, testId } = param;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetCustomPageTemplateRowTID(testId), [
        testId
    ]);
    const businessId = useStore(selectors.user.businessId);
    const { showModal } = useOverlayContext();
    const { deleteTemplate } = useDeleteCustomPageTemplate();
    const { duplicateTemplate } = useDuplicateCustomPageTemplate();
    const typeConfig = getTypeConfig(template.type, t.customPages);
    const handleEdit = ()=>{
        onEdit(template);
    };
    const handleDelete = ()=>{
        showModal({
            biName: 'delete_template_confirm',
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    testId: testIds.CustomPageTemplateRowTestIds.DeleteConfirmModal(),
                    skin: "danger",
                    title: t.customPages.templates.deleteConfirmTitle(),
                    content: /*#__PURE__*/ _jsx(Typography, {
                        children: t.customPages.templates.deleteConfirmMessage()
                    }),
                    primaryButtonLabel: t.customPages.templates.delete(),
                    secondaryButtonLabel: t.customPages.templates.cancel(),
                    onPrimaryButtonClick: ()=>{
                        deleteTemplate({
                            businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                            templateId: template.id
                        });
                        dismissModal();
                    },
                    onSecondaryButtonClick: dismissModal,
                    onCloseButtonClick: dismissModal,
                    bi: {
                        primaryButton: {
                            name: 'delete_template_confirm'
                        },
                        secondaryButton: {
                            name: 'delete_template_cancel'
                        },
                        closeButton: {
                            name: 'delete_template_close'
                        }
                    }
                });
            }
        });
    };
    const handleDuplicate = ()=>{
        duplicateTemplate({
            businessId: businessId !== null && businessId !== void 0 ? businessId : '',
            templateId: template.id
        });
    };
    const Icon = typeConfig.icon;
    return /*#__PURE__*/ _jsxs(Box, {
        testId: testIds.CustomPageTemplateRowTestIds.Root(),
        className: s.root,
        children: [
            /*#__PURE__*/ _jsxs(Box, {
                className: s.leftSection,
                children: [
                    /*#__PURE__*/ _jsx(Box, {
                        testId: testIds.CustomPageTemplateRowTestIds.TypeIcon(),
                        className: cn(s.iconWrapper, typeConfig.iconWrapperClassName),
                        children: /*#__PURE__*/ _jsx(Icon, {
                            size: 24
                        })
                    }),
                    /*#__PURE__*/ _jsxs(Box, {
                        className: s.nameSection,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                testId: testIds.CustomPageTemplateRowTestIds.TemplateName(),
                                skin: "secondary",
                                weight: "normal",
                                variant: "paragraph1",
                                ellipsis: true,
                                children: template.name
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                testId: testIds.CustomPageTemplateRowTestIds.TypeLabel(),
                                skin: "secondary",
                                weight: "light",
                                variant: "paragraph1",
                                className: s.typeLabel,
                                children: typeConfig.label
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs(Box, {
                className: s.actionsSection,
                children: [
                    template.type === 'text' && /*#__PURE__*/ _jsx(IconButton, {
                        skin: "transparent",
                        size: "smallMedium",
                        testId: testIds.CustomPageTemplateRowTestIds.DuplicateButton(),
                        bi: {
                            name: 'duplicate_template'
                        },
                        tooltip: {
                            title: t.customPages.templates.duplicateTooltip()
                        },
                        onClick: handleDuplicate,
                        children: /*#__PURE__*/ _jsx(Copy, {
                            size: 20
                        })
                    }),
                    /*#__PURE__*/ _jsx(IconButton, {
                        skin: "transparent",
                        size: "smallMedium",
                        testId: testIds.CustomPageTemplateRowTestIds.EditButton(),
                        bi: {
                            name: 'edit_template'
                        },
                        tooltip: {
                            title: t.customPages.templates.editTooltip()
                        },
                        onClick: handleEdit,
                        children: /*#__PURE__*/ _jsx(Edit, {
                            size: 20
                        })
                    }),
                    /*#__PURE__*/ _jsx(IconButton, {
                        skin: "transparent",
                        size: "smallMedium",
                        testId: testIds.CustomPageTemplateRowTestIds.DeleteButton(),
                        bi: {
                            name: 'delete_template'
                        },
                        tooltip: {
                            title: t.customPages.templates.deleteTooltip()
                        },
                        onClick: handleDelete,
                        children: /*#__PURE__*/ _jsx(Trash, {
                            size: 20
                        })
                    })
                ]
            })
        ]
    });
};
